import type { Component } from 'solid-js'
import { createSignal, For, Show } from 'solid-js'
import { Trans } from '@mbarzda/solid-i18next'
import { useUser } from '../../shared/providers/user-provider'
import { useCorisReports } from '../../shared/providers/coris/coris-reports.provider'
import { ReportType } from '../../features/reports/domain/interactors/download-reports.interactor'
import { Icon } from '@iconify-icon/solid'

interface ReportItem {
  type: ReportType | 'all'
  titleKey: string
  descriptionKey: string
  bronzeOnly?: boolean
}

const REPORT_ITEMS: ReportItem[] = [
  { type: 'all', titleKey: 'ls_reports_all_title', descriptionKey: 'ls_reports_all_description' },
  {
    type: ReportType.FreeUsers,
    titleKey: 'ls_reports_free_users_title',
    descriptionKey: 'ls_reports_free_users_description'
  },
  {
    type: ReportType.FreeSubscribers,
    titleKey: 'ls_reports_free_subscribers_title',
    descriptionKey: 'ls_reports_free_subscribers_description'
  },
  {
    type: ReportType.IncompleteSignups,
    titleKey: 'ls_reports_incomplete_signups_title',
    descriptionKey: 'ls_reports_incomplete_signups_description'
  },
  {
    type: ReportType.ChurnedSubscribers,
    titleKey: 'ls_reports_churned_subscribers_title',
    descriptionKey: 'ls_reports_churned_subscribers_description'
  },
  {
    type: ReportType.GoldSubscribers,
    titleKey: 'ls_reports_gold_subscribers_title',
    descriptionKey: 'ls_reports_gold_subscribers_description'
  },
  {
    type: ReportType.FreeGoldUpgrades,
    titleKey: 'ls_reports_free_gold_upgrades_title',
    descriptionKey: 'ls_reports_free_gold_upgrades_description'
  },
  {
    type: ReportType.BronzeSubscribers,
    titleKey: 'ls_reports_bronze_subscribers_title',
    descriptionKey: 'ls_reports_bronze_subscribers_description',
    bronzeOnly: true
  },
  {
    type: ReportType.BronzeGoldUpgrades,
    titleKey: 'ls_reports_bronze_gold_upgrades_title',
    descriptionKey: 'ls_reports_bronze_gold_upgrades_description',
    bronzeOnly: true
  },
  {
    type: ReportType.UserEngagementIos,
    titleKey: 'ls_reports_user_engagement_ios_title',
    descriptionKey: 'ls_reports_user_engagement_ios_description'
  },
  {
    type: ReportType.UserEngagementWebTop1Percent,
    titleKey: 'ls_reports_user_engagement_web_top_1_percent_title',
    descriptionKey: 'ls_reports_user_engagement_web_top_1_percent_description'
  },
  {
    type: ReportType.UserEngagementWebTop10Percent,
    titleKey: 'ls_reports_user_engagement_web_top_10_percent_title',
    descriptionKey: 'ls_reports_user_engagement_web_top_10_percent_description'
  },
  {
    type: ReportType.AnnualGiftEligibleSubscribers,
    titleKey: 'ls_reports_annual_gift_eligible_title',
    descriptionKey: 'ls_reports_annual_gift_eligible_description'
  },
  {
    type: ReportType.PaidSubscribersDetail,
    titleKey: 'ls_reports_paid_subscribers_detail_title',
    descriptionKey: 'ls_reports_paid_subscribers_detail_description'
  },
  {
    type: ReportType.MemberStatus,
    titleKey: 'ls_reports_member_status_title',
    descriptionKey: 'ls_reports_member_status_description'
  }
]

const BRONZE_ORG_SLUG = 'brandicarlile'

const Reports: Component = () => {
  const { organizationSlug } = useUser()
  const { downloadReports } = useCorisReports()
  const [downloadingType, setDownloadingType] = createSignal<string | null>(null)

  const visibleReports = () => {
    const isBronzeOrg = organizationSlug() === BRONZE_ORG_SLUG
    return REPORT_ITEMS.filter((item) => !item.bronzeOnly || isBronzeOrg)
  }

  const availableReportTypes = () => {
    return visibleReports()
      .filter((item) => item.type !== 'all')
      .map((item) => item.type as ReportType)
  }

  const handleDownload = async (item: ReportItem) => {
    const organization = organizationSlug()
    if (!organization || downloadingType() !== null) return

    setDownloadingType(item.type)
    try {
      const reportTypes = item.type === 'all' ? availableReportTypes() : [item.type as ReportType]
      await downloadReports.execute(organization, reportTypes)
    } catch (error) {
      console.error('Failed to download reports:', error)
    } finally {
      setDownloadingType(null)
    }
  }

  return (
    <div class="flex flex-col gap-4 p-6">
      <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-4">
        <div class="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <For each={visibleReports()}>
            {(item, index) => (
              <div
                class={`flex items-center justify-between gap-4 px-5 py-4${
                  index() > 0 ? ' border-t border-gray-200' : ''
                }${item.type === 'all' ? ' bg-gray-50' : ''}`}
              >
                <div class="flex min-w-0 flex-col gap-0.5">
                  <h3 class={`text-sm text-gray-900${item.type === 'all' ? ' font-semibold' : ' font-medium'}`}>
                    <Trans key={item.titleKey} />
                  </h3>
                  <p class="text-xs leading-relaxed text-gray-500">
                    <Trans key={item.descriptionKey} />
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleDownload(item)}
                  disabled={downloadingType() !== null}
                  class="btn btn--primary flex shrink-0 items-center gap-2 rounded-md text-sm disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Show
                    when={downloadingType() !== item.type}
                    fallback={
                      <div
                        class="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
                        aria-hidden="true"
                      />
                    }
                  >
                    <Icon icon="heroicons:document-arrow-down" class="text-[1rem]" />
                  </Show>
                  <Trans key="ls_reports_download" />
                </button>
              </div>
            )}
          </For>
        </div>
      </div>
    </div>
  )
}

export default Reports
